import React from "react";
import type { ApolloClient } from "@apollo/client";
import StepWizard from "components/StepWizard";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { find } from "lodash";
import StepInfo from "components/StepInfo";
import GroupSelection from "components/groupMigrationWorkflow/GroupSelection";
import EmployeeSelection from "components/groupMigrationWorkflow/EmployeeSelection";
import Summary from "components/groupMigrationWorkflow/Summary";
import { migrateEmployees } from "actions/adminActions";
import { adminConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { toast } from "react-toastify";
import type { RootState } from "types/state";

const stepToLabels = {
  1: {
    subheader:
      "Choose which group you are migrating from and which group you want to migrate to",
  },
  2: {
    subheader: "Select which employees are being migrated to the new group",
  },
  3: {
    subheader: "Here's the summary.",
  },
};

const steps = [
  {
    index: 1,
    label: "Select groups for migration",
  },
  {
    index: 2,
    label: "Select employees",
  },
  {
    index: 3,
    label: "Submit",
  },
];

const actions = [adminConstants.ADMIN_MIGRATE_EMPLOYEES];

const isUpdatingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

/** A migratable group. `groups` was `PropTypes.array`, but it is passed
 *  straight to `GroupSelection`/`EmployeeSelection` (which type it as a
 *  `Record<string, Group>`) and `find`'d here to feed `Summary`. The shape is
 *  the union of those consumers' requirements. */
interface MigrationGroup {
  id: string;
  name: string;
  associatedLinkRequests: Array<{ employeeId: string; [key: string]: unknown }>;
  payrollFrequency?: unknown;
  [key: string]: unknown;
}

interface GroupMigrationWorkflowOwnProps {
  // getLinkedEmployees: PropTypes.func — declared but unused in this component.
  getLinkedEmployees?: (...args: unknown[]) => void;
  groups: Record<string, MigrationGroup>;
  onCancel?: (...args: unknown[]) => void;
  companyId?: string;
  // onSuccess: PropTypes.func — invoked unconditionally on success below.
  onSuccess: (data?: unknown) => void;
}

interface GroupMigrationWorkflowState {
  sourceGroupId: string;
  targetGroupId: string;
  employeesForMigration: string[];
  // Initialized to the string "1" but StepWizard's onStepChange reports the
  // active step as a number, so both flow into state at runtime.
  activeStep: string | number;
}

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    isUpdating: isUpdatingSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  migrateEmployees,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type GroupMigrationWorkflowProps = PropsFromRedux &
  GroupMigrationWorkflowOwnProps;

export class GroupMigrationWorkflow extends React.PureComponent<
  WithApolloClient<GroupMigrationWorkflowProps>,
  GroupMigrationWorkflowState
> {
  constructor(props: WithApolloClient<GroupMigrationWorkflowProps>) {
    super(props);

    this.state = {
      sourceGroupId: "",
      targetGroupId: "",
      employeesForMigration: [],
      activeStep: "1",
    };
  }

  _onStepChange = ({ activeStep }: { activeStep: number }) => {
    this.setState({ activeStep });
  };

  _setTargetGroupId = (targetGroupId: string) => {
    this.setState({ targetGroupId });
  };

  _setSourceGroupId = (sourceGroupId: string) => {
    this.setState({ sourceGroupId });
  };

  _setEmployeesForMigration = (employeesForMigration: string[]) => {
    this.setState({ employeesForMigration });
  };

  _submitGroupMigration = () => {
    const { sourceGroupId, targetGroupId, employeesForMigration } = this.state;
    void this.props
      .migrateEmployees(this.props.client as ApolloClient<object>, {
        sourceGroupId,
        targetGroupId,
        employeesForMigration,
        // companyId is an optional prop (PropTypes.string) but the mutation
        // variable is required — preserves the original (possibly-undefined) call.
        // @ts-expect-error optional prop passed to required mutation variable
        companyId: this.props.companyId,
      })
      .then((result) => {
        if (result.success) {
          toast.success("Employees migrated successfully");
          this.props.onSuccess(result.data);
        } else {
          toast.error("Error migrating employees");
        }
      });
  };

  render() {
    // stepToLabels is keyed by numeric literals 1|2|3 while activeStep is a
    // string|number; the lookup relies on JS index coercion. Cast the key to
    // the object's key type to keep the result typed without changing behavior.
    const activeSectionLabels =
      stepToLabels[this.state.activeStep as keyof typeof stepToLabels];

    const sourceGroup = find(this.props.groups, {
      id: this.state.sourceGroupId,
    });
    const targetGroup = find(this.props.groups, {
      id: this.state.targetGroupId,
    });
    return (
      <div className="mega-container" id="payroll-workflow">
        <section className="page-title-wrap">
          <article className="text-cell">
            <div className="subtext-and-calendar">
              <p className="page-subtext">{activeSectionLabels.subheader}</p>
            </div>
            <StepInfo steps={steps} activeStepNumber={this.state.activeStep} />
          </article>
        </section>
        <div className="main-content">
          <StepWizard
            onStepChange={this._onStepChange}
            isLazyMount={true}
            transitions={{
              enterRight: "",
              enterLeft: "",
              exitRight: "",
              exitLeft: "",
            }}
          >
            <GroupSelection
              groups={this.props.groups}
              onCancel={this.props.onCancel}
              stepName="selectGroups"
              setSourceGroupId={this._setSourceGroupId}
              setTargetGroupId={this._setTargetGroupId}
              sourceGroupId={this.state.sourceGroupId}
              targetGroupId={this.state.targetGroupId}
            />
            <EmployeeSelection
              // find() may return undefined; EmployeeSelection requires it
              // present — assert it (a missing group would crash the step,
              // exactly as in the original JS).
              sourceGroup={sourceGroup!}
              stepName="employeeSelection"
              setEmployeesForMigration={this._setEmployeesForMigration}
              employeesForMigration={this.state.employeesForMigration}
            />
            <Summary
              stepName={"summary"}
              // find() may return undefined; Summary requires these present —
              // asserted (behavior unchanged from the JS).
              sourceGroup={sourceGroup!}
              targetGroup={targetGroup!}
              handleMigration={this._submitGroupMigration}
              isUpdating={this.props.isUpdating}
              employeesForMigration={this.state.employeesForMigration}
            />
          </StepWizard>
        </div>
      </div>
    );
  }
}

export default connector(
  withApollo<GroupMigrationWorkflowProps>(GroupMigrationWorkflow)
);
